import { useState } from 'react';
import { 
  Card, 
  CardContent, 
  CardHeader, 
  CardTitle, 
  CardDescription 
} from "@/components/ui/card";
import { 
  Tabs, 
  TabsContent, 
  TabsList, 
  TabsTrigger 
} from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { 
  ArrowUp, 
  ArrowDown, 
  Star, 
  Puzzle, 
  ThumbsUp, 
  HelpCircle,
  Calculator,
  Search,
  PlusCircle,
  Archive,
  BarChart4,
  FileText,
  ShoppingCart
} from "lucide-react";
import { PricingAlert } from "@/components/supplier/PricingAlert";
import { NegotiationOpportunity } from "@/components/supplier/NegotiationOpportunity";

// Sample data
const inventoryItems = [
  { 
    id: 1, 
    name: "Carne Picada", 
    category: "Meat", 
    quantity: 25.5, 
    unit: "kg", 
    par: 35, 
    costPerUnit: 8.49, 
    supplier: "Carnicería Atlántica",
    lastOrdered: "2023-05-15",
    shelfLife: "7 days",
    status: "normal"
  },
  { 
    id: 2, 
    name: "Cabra Canaria", 
    category: "Poultry", 
    quantity: 12.3, 
    unit: "kg", 
    par: 20, 
    costPerUnit: 6.99, 
    supplier: "Distribuciones Cárnicas Insulares",
    lastOrdered: "2023-05-12",
    shelfLife: "5 days",
    status: "low"
  },
  { 
    id: 3, 
    name: "Aceite de Oliva", 
    category: "Dry Goods", 
    quantity: 8, 
    unit: "liters", 
    par: 10, 
    costPerUnit: 15.99, 
    supplier: "Importaciones El Teide",
    lastOrdered: "2023-04-30",
    shelfLife: "12 months",
    status: "normal"
  },
  { 
    id: 4, 
    name: "Tomates Canarios", 
    category: "Produce", 
    quantity: 5.7, 
    unit: "kg", 
    par: 15, 
    costPerUnit: 3.49, 
    supplier: "Finca Agrícola Canaria",
    lastOrdered: "2023-05-14",
    shelfLife: "7 days",
    status: "critical"
  },
  { 
    id: 5, 
    name: "Nata Montada", 
    category: "Dairy", 
    quantity: 4, 
    unit: "liters", 
    par: 6, 
    costPerUnit: 4.29, 
    supplier: "Lácteos Guanche",
    lastOrdered: "2023-05-13",
    shelfLife: "14 days",
    status: "low"
  }
];

const pricingAlerts = [
  {
    id: 1,
    supplier: "Carnicería Atlántica",
    product: "Carne Picada",
    yourPrice: 8.49,
    competitorPrice: 7.19,
    priceDiff: 18,
    competitor: "Restaurante El Médano",
    actionTaken: false,
  },
  {
    id: 2,
    supplier: "Finca Agrícola Canaria",
    product: "Espinacas Ecológicas",
    yourPrice: 12.99,
    competitorPrice: 10.25,
    priceDiff: 27,
    competitor: "Casa Vieja",
    actionTaken: true,
  }
];

const negotiationOpportunities = [
  {
    id: 1,
    supplier: "Importaciones El Teide",
    category: "Dry Goods",
    product: "Aceite de Oliva Premium (5L)",
    currentPrice: 89.99,
    marketRate: 75.50,
    percentAbove: 19,
    potentialSavings: 1725.84,
    volumeHistory: "High & Consistent"
  },
  {
    id: 2,
    supplier: "Pescados Isleños",
    category: "Seafood",
    product: "Filetes de Vieja",
    currentPrice: 22.99,
    marketRate: 19.75,
    percentAbove: 16,
    potentialSavings: 3864.00,
    volumeHistory: "Medium & Growing"
  }
];

const InventoryIntelligence = () => {
  const [searchTerm, setSearchTerm] = useState("");

  const filteredItems = inventoryItems.filter(item => 
    item.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    item.category.toLowerCase().includes(searchTerm.toLowerCase()) ||
    item.supplier.toLowerCase().includes(searchTerm.toLowerCase())
  );
  
  return (
    <div className="space-y-8 animate-fade-in">
      <div className="flex items-center justify-between">
        <h1 className="text-3xl font-bold">Inventory Intelligence</h1>
        <div className="flex gap-3">
          <Button variant="outline" size="sm" className="h-9">
            <FileText className="mr-2 h-4 w-4" /> Export
          </Button>
          <Button size="sm" className="h-9">
            <PlusCircle className="mr-2 h-4 w-4" /> Add Item
          </Button>
        </div>
      </div>

      <Tabs defaultValue="inventory" className="w-full">
        <TabsList className="grid w-full grid-cols-4">
          <TabsTrigger value="inventory">Current Inventory</TabsTrigger>
          <TabsTrigger value="orders">Orders</TabsTrigger>
          <TabsTrigger value="insights">Price Insights</TabsTrigger>
          <TabsTrigger value="analytics">Analytics</TabsTrigger>
        </TabsList>

        <TabsContent value="inventory" className="space-y-6">
          <Card>
            <CardHeader className="pb-4">
              <div className="flex flex-col sm:flex-row justify-between sm:items-center gap-4">
                <CardTitle>Inventory Items</CardTitle>
                <div className="relative w-full sm:w-72">
                  <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
                  <Input
                    type="search"
                    placeholder="Search items..."
                    className="pl-8"
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                  />
                </div>
              </div>
            </CardHeader>
            <CardContent>
              <div className="border rounded-lg overflow-hidden">
                <table className="w-full text-sm">
                  <thead className="bg-muted/50">
                    <tr>
                      <th className="text-left p-3 font-medium">Item</th>
                      <th className="text-left p-3 font-medium">Category</th>
                      <th className="text-center p-3 font-medium">Quantity</th>
                      <th className="text-center p-3 font-medium">Par Level</th>
                      <th className="text-right p-3 font-medium">Unit Cost</th>
                      <th className="text-right p-3 font-medium">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y">
                    {filteredItems.map((item) => (
                      <tr key={item.id} className="hover:bg-muted/30">
                        <td className="p-3">
                          <div className="font-medium">{item.name}</div>
                          <div className="text-xs text-muted-foreground">{item.supplier}</div>
                        </td>
                        <td className="p-3">{item.category}</td>
                        <td className="p-3 text-center">
                          <div className="flex flex-col items-center">
                            <div className="flex items-center gap-1.5">
                              <span>{item.quantity} {item.unit}</span>
                              {item.status === "critical" && (
                                <Badge variant="destructive" className="text-[10px] px-1 py-0">Critical</Badge>
                              )}
                              {item.status === "low" && (
                                <Badge variant="secondary" className="bg-amber-100 text-amber-800 hover:bg-amber-100">
                                  Low Stock
                                </Badge>
                              )}
                            </div>
                            <div className="w-16 h-1.5 bg-muted mt-1.5 rounded-full overflow-hidden">
                              <div 
                                className={`h-full ${
                                  (item.quantity / item.par) < 0.3 
                                    ? "bg-red-500" 
                                    : (item.quantity / item.par) < 0.7 
                                      ? "bg-amber-500" 
                                      : "bg-green-500"
                                }`}
                                style={{ width: `${Math.min(100, (item.quantity / item.par) * 100)}%` }}
                              />
                            </div>
                          </div>
                        </td>
                        <td className="p-3 text-center">{item.par} {item.unit}</td>
                        <td className="p-3 text-right">${item.costPerUnit.toFixed(2)}</td>
                        <td className="p-3 text-right">
                          <div className="flex items-center justify-end gap-2">
                            <Button variant="ghost" size="icon" className="h-8 w-8">
                              <ShoppingCart className="h-4 w-4" />
                            </Button>
                            <Button variant="ghost" size="icon" className="h-8 w-8">
                              <Archive className="h-4 w-4" />
                            </Button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-base">Critical Items</CardTitle>
                <CardDescription>Items needing immediate attention</CardDescription>
              </CardHeader>
              <CardContent>
                <ul className="space-y-3">
                  {inventoryItems
                    .filter(item => item.status === "critical")
                    .map(item => (
                      <li key={item.id} className="flex items-center justify-between">
                        <div>
                          <p className="font-medium">{item.name}</p>
                          <p className="text-xs text-muted-foreground">
                            {item.quantity} {item.unit} of {item.par} {item.unit} par
                          </p>
                        </div>
                        <Button variant="destructive" size="sm">Order</Button>
                      </li>
                    ))}
                </ul>
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-base">Low Stock Items</CardTitle>
                <CardDescription>Items running low</CardDescription>
              </CardHeader>
              <CardContent>
                <ul className="space-y-3">
                  {inventoryItems
                    .filter(item => item.status === "low")
                    .map(item => (
                      <li key={item.id} className="flex items-center justify-between">
                        <div>
                          <p className="font-medium">{item.name}</p>
                          <p className="text-xs text-muted-foreground">
                            {item.quantity} {item.unit} of {item.par} {item.unit} par
                          </p>
                        </div>
                        <Button variant="outline" size="sm">Order</Button>
                      </li>
                    ))}
                </ul>
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-base">Approaching Expiry</CardTitle>
                <CardDescription>Use these items soon</CardDescription>
              </CardHeader>
              <CardContent>
                <ul className="space-y-3">
                  <li className="flex items-center justify-between">
                    <div>
                      <p className="font-medium">Diced Tomatoes</p>
                      <p className="text-xs text-muted-foreground">
                        Expires in 2 days
                      </p>
                    </div>
                    <Badge variant="outline" className="bg-amber-50 text-amber-700 border-amber-200">Use Soon</Badge>
                  </li>
                  <li className="flex items-center justify-between">
                    <div>
                      <p className="font-medium">Fresh Basil</p>
                      <p className="text-xs text-muted-foreground">
                        Expires in 3 days
                      </p>
                    </div>
                    <Badge variant="outline" className="bg-amber-50 text-amber-700 border-amber-200">Use Soon</Badge>
                  </li>
                </ul>
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        <TabsContent value="orders" className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle>Purchase Orders</CardTitle>
              <CardDescription>
                Track your recent and upcoming orders
              </CardDescription>
            </CardHeader>
            <CardContent>
              <p className="text-sm text-muted-foreground">
                Purchase order content will appear here.
              </p>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="insights" className="space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="space-y-6">
              <h3 className="text-lg font-medium">Pricing Alerts</h3>
              <div className="grid grid-cols-1 gap-4">
                {pricingAlerts.map(alert => (
                  <PricingAlert key={alert.id} alert={alert} />
                ))}
              </div>
            </div>
            
            <div className="space-y-6">
              <h3 className="text-lg font-medium">Negotiation Opportunities</h3>
              <div className="grid grid-cols-1 gap-4">
                {negotiationOpportunities.map(opportunity => (
                  <NegotiationOpportunity key={opportunity.id} opportunity={opportunity} />
                ))}
              </div>
            </div>
          </div>
        </TabsContent>

        <TabsContent value="analytics" className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle>Usage Analytics</CardTitle>
              <CardDescription>
                Inventory usage patterns and insights
              </CardDescription>
            </CardHeader>
            <CardContent>
              <p className="text-sm text-muted-foreground">
                Analytics content will appear here.
              </p>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default InventoryIntelligence;
